import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { Bot, Trophy, Copy, Check, Clock, Hash } from 'lucide-react';
import { MODELS } from '../lib/models.js';
import { fmtMs, fmtTokens, fmtUSD } from '../lib/estimate.js';

export function ResponseTimeline({ result, running }) {
  if (!result && !running) return null;

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55 }}
      className="panel p-5"
    >
      <div className="mb-4 flex items-center justify-between">
        <div className="section-title">
          <Bot className="h-4 w-4 text-accent-cyan" />
          Agent responses
          {running && <span className="chip-cyan animate-pulse-glow">orchestrating…</span>}
        </div>
        {result?.totals && (
          <div className="flex items-center gap-2 font-mono text-[10px] text-ink-dim">
            <Clock className="h-3 w-3" /> {fmtMs(result.totals.elapsedMs)}
            <span className="opacity-60">·</span>
            <Hash className="h-3 w-3" /> {fmtTokens(result.totals.totalTokens)}
            <span className="opacity-60">·</span>
            <span className="text-accent-orange-soft">{fmtUSD(result.totals.totalCost)}</span>
          </div>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <AnimatePresence>
          {result?.responses?.map((r, i) => (
            <ResponseCard key={i} index={i} response={r} winner={result.synthesis?.winnerIndex === i} />
          ))}
          {running && !result && [0, 1, 2].map((i) => <SkeletonCard key={i} index={i} />)}
        </AnimatePresence>
      </div>

      {result?.synthesis && <SynthesisCard synthesis={result.synthesis} />}
    </motion.section>
  );
}

function ResponseCard({ index, response, winner }) {
  const [copied, setCopied] = useState(false);
  const m = MODELS[response.model];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(response.content || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {}
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={{ duration: 0.35, delay: index * 0.05 }}
      className={`relative flex flex-col rounded-2xl border bg-bg-deep/60 ${winner ? 'border-accent-orange/60 shadow-glow-orange' : 'border-border-subtle'}`}
    >
      {winner && (
        <div className="absolute -top-2.5 left-3 flex items-center gap-1 rounded-full bg-gradient-to-r from-accent-orange to-accent-orange-dark px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-bg-deep shadow-glow-orange">
          <Trophy className="h-3 w-3" /> selected
        </div>
      )}
      <div className="flex items-center justify-between border-b border-border-subtle/60 px-4 py-2.5">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ background: m?.color || '#94a8c4' }} />
          <span className="text-sm font-medium text-ink-primary">{m?.label || response.model}</span>
          <span className="font-mono text-[10px] text-ink-dim">T{m?.tier ?? '?'}</span>
        </div>
        <button onClick={copy} className="text-ink-dim transition hover:text-accent-cyan-light" title="Copy">
          {copied ? <Check className="h-3.5 w-3.5 text-signal-ok" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
      </div>
      <div className="scrollbar-stealth max-h-[420px] overflow-y-auto whitespace-pre-wrap px-4 py-3 text-sm leading-relaxed text-ink-primary">
        {response.content || <span className="italic text-ink-dim">empty response</span>}
      </div>
      <div className="flex items-center justify-between border-t border-border-subtle/40 px-4 py-2 font-mono text-[10px] text-ink-dim">
        <span>{fmtTokens(response.tokens?.input)} in · {fmtTokens(response.tokens?.output)} out</span>
        <span className="text-accent-orange-soft">{fmtUSD(response.cost)}</span>
        {response.elapsedMs != null && <span>{fmtMs(response.elapsedMs)}</span>}
      </div>
    </motion.div>
  );
}

function SkeletonCard({ index }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: index * 0.05 }}
      className="overflow-hidden rounded-2xl border border-border-subtle bg-bg-deep/60"
    >
      <div className="flex items-center justify-between border-b border-border-subtle/60 px-4 py-2.5">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-accent-cyan" />
          <span className="h-3 w-24 animate-shimmer rounded bg-gradient-to-r from-bg-surface via-bg-surface2 to-bg-surface bg-[length:1000px_100%]" />
        </div>
      </div>
      <div className="space-y-2 p-4">
        {[100, 95, 80, 92, 70].map((w, i) => (
          <div
            key={i}
            className="h-3 animate-shimmer rounded bg-gradient-to-r from-bg-surface via-bg-surface2 to-bg-surface bg-[length:1000px_100%]"
            style={{ width: `${w}%` }}
          />
        ))}
      </div>
    </motion.div>
  );
}

function SynthesisCard({ synthesis }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45 }}
      className="mt-5 rounded-2xl border border-accent-orange/40 bg-gradient-to-br from-accent-orange/10 via-transparent to-accent-cyan/10 p-5 shadow-glow-orange"
    >
      <div className="mb-2 flex items-center gap-2">
        <Trophy className="h-4 w-4 text-accent-orange" />
        <span className="font-semibold text-ink-primary">Judge synthesis</span>
        {synthesis.judgeModel && (
          <span className="chip-orange">
            <span className="font-mono">{synthesis.judgeModel}</span>
          </span>
        )}
      </div>
      <div className="whitespace-pre-wrap text-sm leading-relaxed text-ink-primary">
        {synthesis.content}
      </div>
      {synthesis.rationale && (
        <details className="mt-3 text-xs text-ink-muted">
          <summary className="cursor-pointer font-mono text-[10px] uppercase tracking-[0.16em] text-ink-dim hover:text-accent-cyan-light">
            judge rationale
          </summary>
          <div className="mt-2 whitespace-pre-wrap rounded-lg bg-bg-deep/60 p-3 font-mono text-[11px]">
            {synthesis.rationale}
          </div>
        </details>
      )}
    </motion.div>
  );
}
